import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceLine,
} from "recharts";
import {
  Atom, Flame, Zap, Activity, AlertTriangle, BookOpen, Sparkles, FlaskConical,
} from "lucide-react";
import PricingWidget from "@/components/portal/PricingWidget";
import { useI18n } from "@/lib/i18n";

// ===== EPS BASE PROPERTIES (pure unmodified EPS) =====
// Literature: ISO 8301, Schmied 2018 (Silver EPS), Du 2010 (HNT), Stankovich 2006 (GNP)
const EPS_BASE = {
  lambda: 0.038,                 // W/mK
  loi: 24,                       // %
  ul94: "HB",
  pHRR: 350,                     // kW/m² (Cone Calorimeter, 50 kW/m² irradiance)
  flexural: 200,                 // kPa (TS EN 12089)
  tensile: 130,                  // kPa (TS EN 1607)
  density: 17,                   // kg/m³
};

// ===== ADDITIVE LIBRARY (sensitivity coefficients per 1% additive) =====
// Coefficients calibrated to published academic trends for EPS-matrix composites.
const ADDITIVES = {
  bor: {
    name: "Boron Compounds",
    nameKey: "eps.bor.name",
    short: "B",
    chem: "Zn-Borate / H₃BO₃",
    color: "#FFCC00",
    icon: Flame,
    range: [0, 15],
    step: 0.5,
    coeff: {
      lambda:  +0.00025,   // mild rise (density)
      loi:     +0.85,      // strong char + glass-formation
      pHRR:    -9.0,       // strong intumescent effect
      flexural:-1.6,       // filler-induced mild loss
      tensile: -1.2,
      density: +0.45,
    },
    note: "Bor bileşikleri (özellikle çinko borat ve borik asit) EPS matrisinde yüksek sıcaklıkta camsı koruyucu tabaka oluşturarak alev yayılımını dramatik şekilde yavaşlatır. LOI'yi %15 mertebesinde artırır.",
  },
  grafit: {
    name: "Graphite (Carbon)",
    nameKey: "eps.grafit.name",
    short: "C",
    chem: "Graphite (silver EPS)",
    color: "#00E5FF",
    icon: Zap,
    range: [0, 10],
    step: 0.25,
    coeff: {
      lambda:  -0.0009,    // IR opacification ↓ λ
      loi:     +0.10,
      pHRR:    -3.0,
      flexural:+0.6,
      tensile: +0.5,
      density: +0.20,
    },
    note: "Grafit partikülleri kızılötesi radyasyon transferini bloke ederek 'Silver EPS' efekti ile termal iletkenliği %15-25 düşürür.",
  },
  grafen: {
    name: "Graphene / GNP",
    nameKey: "eps.grafen.name",
    short: "G",
    chem: "Graphene Nano-Platelets",
    color: "#39FF14",
    icon: Atom,
    range: [0, 3],
    step: 0.05,
    coeff: {
      lambda:  -0.0012,    // low % thermal IR barrier
      loi:     +1.4,
      pHRR:    -12.0,
      flexural:+18.0,      // nano-reinforcement
      tensile: +14.0,
      density: +0.15,
    },
    note: "Grafen, 0.5-2% mertebesinde EPS matrisine eklendiğinde hem ısıl performansı hem mekanik dayanımı 'yüksek-uç' (high-end) seviyeye taşır.",
  },
  hnt: {
    name: "HNT Kili (Halloysite)",
    short: "HNT",
    chem: "Al₂Si₂O₅(OH)₄ · 2H₂O",
    color: "#00E5FF",
    icon: FlaskConical,
    range: [0, 8],
    step: 0.25,
    coeff: {
      lambda:  +0.00015,
      loi:     +0.75,
      pHRR:    -7.5,
      flexural:+6.0,
      tensile: +4.5,
      density: +0.30,
    },
    note: "Halloysit nanotüpleri, doğal kil yapısının nanoboyutlu tüpsel formuyla EPS matrisinde silikat bariyeri oluşturur — yangın geciktirici, mekanik takviye ve düşük maliyet üçlüsünü tek katkıda birleştirir.",
  },
};

function ul94FromLoi(loi) {
  if (loi >= 32) return "V-0";
  if (loi >= 28) return "V-1";
  if (loi >= 25) return "V-2";
  return "HB";
}

function ul94Color(rating) {
  if (rating === "V-0") return "#39FF14";
  if (rating === "V-1") return "#7CFF14";
  if (rating === "V-2") return "#FFCC00";
  return "#FF8C00";
}

// Apply additive effects to EPS_BASE
function predictEPS(mix) {
  // mix: { bor, grafit, grafen, hnt } in %
  let lambda = EPS_BASE.lambda;
  let loi = EPS_BASE.loi;
  let pHRR = EPS_BASE.pHRR;
  let flexural = EPS_BASE.flexural;
  let tensile = EPS_BASE.tensile;
  let density = EPS_BASE.density;

  for (const [k, pct] of Object.entries(mix)) {
    const a = ADDITIVES[k];
    if (!a) continue;
    lambda += a.coeff.lambda * pct;
    loi    += a.coeff.loi * pct;
    pHRR   += a.coeff.pHRR * pct;
    flexural += a.coeff.flexural * pct;
    tensile  += a.coeff.tensile * pct;
    density  += a.coeff.density * pct;
  }
  loi = Math.max(15, loi);
  pHRR = Math.max(40, pHRR);
  flexural = Math.max(50, flexural);
  tensile = Math.max(20, tensile);
  return {
    lambda: Number(lambda.toFixed(4)),
    loi: Number(loi.toFixed(1)),
    ul94: ul94FromLoi(loi),
    pHRR: Math.round(pHRR),
    flexural: Math.round(flexural),
    tensile: Math.round(tensile),
    density: Number(density.toFixed(1)),
  };
}

// Sensitivity sweep: hold others constant, sweep one additive from 0..max
function sweep(mix, additiveKey, points = 20) {
  const a = ADDITIVES[additiveKey];
  const [lo, hi] = a.range;
  const out = [];
  for (let i = 0; i <= points; i++) {
    const v = lo + ((hi - lo) * i) / points;
    const m = { ...mix, [additiveKey]: v };
    const p = predictEPS(m);
    out.push({
      x: Number(v.toFixed(2)),
      lambda: p.lambda,
      loi: p.loi,
      flexural: p.flexural,
    });
  }
  return out;
}

// Tiny sparkline chart — every label/value/unit shares the same vibrant `color`
function Spark({ data, yKey, color, label, unit, currentX, format }) {
  const last = data[data.length - 1];
  return (
    <div className="rd-chart-container border border-[#1F1F1F] bg-[#050505] p-2" data-chart="true" data-chart-text-blue="true" style={{ backgroundColor: '#000000' }}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-[9px] uppercase tracking-wider"
          style={{ color: '#FFFF00', WebkitTextFillColor: '#FFFF00', opacity: 1, fontWeight: 700 }}>{label}</span>
        <span className="font-mono text-[9px]"
          style={{ color: '#FFFF00', WebkitTextFillColor: '#FFFF00', opacity: 1, fontWeight: 700 }}>
          0 → {data[data.length - 1].x}
        </span>
      </div>
      <div style={{ height: 56 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 3, right: 2, bottom: 0, left: -28 }}>
            <CartesianGrid stroke="#0F0F0F" strokeDasharray="0"/>
            <XAxis dataKey="x" hide/>
            <YAxis hide domain={["dataMin", "dataMax"]}/>
            <Line type="linear" dataKey={yKey} stroke={color} strokeWidth={1.6} dot={false} isAnimationActive={false}/>
            <ReferenceLine x={currentX} stroke="#FFFFFF" strokeWidth={0.6} strokeDasharray="2 2"/>
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="font-mono text-[10px]"
        style={{ color: '#FFFF00', WebkitTextFillColor: '#FFFF00', opacity: 1, fontWeight: 700 }}>
        {format ? format(last[yKey]) : last[yKey]} {unit}
      </div>
    </div>
  );
}

function ResultCell({ label, value, unit, accent, baseline, betterIsLower }) {
  const delta = (typeof value === "number" && typeof baseline === "number") ? value - baseline : null;
  const color = '#00E5FF'; // Hoca's Blue — sacred metric color
  return (
    <div className="border border-[#1F1F1F] bg-[#050505] p-3" data-testid={`eps-result-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="tech-label" style={{ color, WebkitTextFillColor: color, fontWeight: 700, opacity: 1 }}>{label}</div>
      <div className="font-mono text-2xl" style={{ color, WebkitTextFillColor: color, fontWeight: 800, opacity: 1 }}>
        {value}
      </div>
      <div className="flex items-center justify-between mt-0.5">
        <span className="font-mono text-[10px]" style={{ color, WebkitTextFillColor: color, fontWeight: 700, opacity: 1 }}>{unit}</span>
        {delta !== null && (
          <span className="font-mono text-[10px]" style={{ color, WebkitTextFillColor: color, fontWeight: 700, opacity: 1 }}>
            {delta > 0 ? "+" : ""}{typeof delta === "number" ? delta.toFixed(Math.abs(delta) < 1 ? 3 : 1) : delta}
          </span>
        )}
      </div>
    </div>
  );
}

export default function EPSSimulationPanel() {
  const { t } = useI18n();
  const [mix, setMix] = useState({ bor: 4, grafit: 3, grafen: 0.5, hnt: 2 });

  const setOne = (k, v) => setMix((m) => ({ ...m, [k]: v }));

  const pred = useMemo(() => predictEPS(mix), [mix]);

  const sweeps = useMemo(() => ({
    bor:    sweep(mix, "bor"),
    grafit: sweep(mix, "grafit"),
    grafen: sweep(mix, "grafen"),
    hnt:    sweep(mix, "hnt"),
  }), [mix]);

  const totalLoad = useMemo(() => mix.bor + mix.grafit + mix.grafen + mix.hnt, [mix]);

  return (
    <section className="border-2 border-[#FF8C42]"
      style={{ backgroundColor: "#0F172A" }}
      data-testid="eps-simulation-panel">
      {/* Header */}
      <header className="border-b px-4 py-2 flex items-center justify-between" style={{ backgroundColor: '#EC4899', borderBottomColor: '#EC4899' }}>
        <div className="flex items-center gap-2">
          <Atom className="w-4 h-4" style={{ color: '#FFFFFF' }}/>
          <span
            className="font-mono text-xs uppercase tracking-[0.2em]"
            style={{ color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF', fontWeight: 700, opacity: 1 }}>
            {t("eps.header.title", "EPS R&D Model · Advanced Additive Library")}
          </span>
        </div>
        <span
          className="font-mono text-[10px] uppercase tracking-wider"
          style={{ color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF', fontWeight: 700, opacity: 0.9 }}>SCIENTIFIC DASHBOARD</span>
      </header>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', padding: '12px', borderRadius: '6px' }}>
            <div className="tech-label" style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 700, opacity: 1 }}>{t("eps.matrix.label", "MATRIX")}</div>
            <div className="font-mono text-sm" style={{ color: '#EC4899', WebkitTextFillColor: '#EC4899', fontWeight: 800, opacity: 1 }}>
              {t("eps.matrix.val", "Expanded Polystyrene (EPS)")}
            </div>
            <div className="font-mono text-[10px] mt-1" style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 500, opacity: 1 }}>ISO 8301 · TS EN 13163</div>
          </div>
          <div style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', padding: '12px', borderRadius: '6px' }}>
            <div className="tech-label" style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 700, opacity: 1 }}>{t("eps.load.label", "TOTAL ADDITIVE LOADING")}</div>
            <div className="font-mono text-lg" style={{ color: '#EC4899', WebkitTextFillColor: '#EC4899', fontWeight: 800, opacity: 1 }}>{totalLoad.toFixed(2)} %</div>
            <div className="font-mono text-[10px] mt-1" style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 500, opacity: 1 }}>
              {totalLoad > 25 ? t("eps.load.high", "⚠ High (filler limit)") : totalLoad > 12 ? t("eps.load.mid", "Moderate") : t("eps.load.low", "Conservative")}
            </div>
          </div>
          <div style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', padding: '12px', borderRadius: '6px' }}>
            <div className="tech-label" style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 700, opacity: 1 }}>{t("eps.fireclass.label", "FIRE CLASS (PREDICTED)")}</div>
            <div className="font-mono text-lg" style={{ color: '#EC4899', WebkitTextFillColor: '#EC4899', fontWeight: 800, opacity: 1 }}>
              UL94 · {pred.ul94}
            </div>
            <div className="font-mono text-[10px] mt-1" style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 500, opacity: 1 }}>
              LOI {pred.loi}% · ASTM D3801
            </div>
          </div>
        </div>

        {/* Additive sliders */}
        <div data-testid="eps-sliders" className="eps-injection-card">
          <div className="eps-injection-header px-3 py-2 border-b font-mono text-[11px] uppercase tracking-[0.2em]"
            style={{ backgroundColor: '#1E40AF', color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF', fontWeight: 700, opacity: 1, borderBottomColor: '#1E40AF' }}>
            {t("eps.injection", "ADDITIVE INJECTION · MASS % (m/m)")}
          </div>
          <div className="p-4 space-y-5">
            {Object.entries(ADDITIVES).map(([key, a]) => {
              const Icon = a.icon;
              const v = mix[key];
              const isHnt = key === 'hnt';
              const headerBg = isHnt ? '#1E40AF' : 'transparent';
              const headerText = isHnt ? '#000000' : a.color;
              const headerPad = isHnt ? '6px 14px' : '0';
              const headerRadius = isHnt ? '8px' : '0';
              return (
                <div key={key} data-testid={`eps-slider-${key}`}>
                  <div className="flex items-center justify-between mb-1" style={{ backgroundColor: headerBg, padding: headerPad, borderRadius: headerRadius }}>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 border flex items-center justify-center" style={{ borderColor: headerText }}>
                        <Icon className="w-3.5 h-3.5" style={{ color: headerText }}/>
                      </div>
                      <div>
                        <div className="font-mono text-xs uppercase tracking-wider font-semibold" style={{ color: headerText, WebkitTextFillColor: headerText, fontWeight: 700, opacity: 1 }}>
                          {a.name}
                        </div>
                        <div className="eps-injection-chem font-mono text-[10px]" style={isHnt ? { color: '#000000', WebkitTextFillColor: '#000000', opacity: 0.85 } : undefined}>{a.chem}</div>
                      </div>
                    </div>
                    <span className="font-mono text-lg font-semibold" style={{ color: headerText, WebkitTextFillColor: headerText, fontWeight: 800, opacity: 1 }}>
                      {v.toFixed(a.step < 1 ? 2 : 0)}%
                    </span>
                  </div>
                  <Slider
                    value={[v]} min={a.range[0]} max={a.range[1]} step={a.step}
                    onValueChange={(arr) => setOne(key, arr[0])}
                    className="my-2"
                  />
                  <div className="eps-injection-meta flex items-center justify-between font-mono text-[10px]">
                    <span>{a.range[0]}% → {a.range[1]}%</span>
                    <span>Δλ: {(a.coeff.lambda * 1000).toFixed(2)}·10⁻³ · ΔLOI: {a.coeff.loi.toFixed(2)} · Δσ: {a.coeff.flexural.toFixed(1)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Results 6-grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-[#1F1F1F] border border-[#1F1F1F]" data-testid="eps-results">
          <ResultCell label="λ" value={pred.lambda} unit="W/mK" accent="#00E5FF" baseline={EPS_BASE.lambda} betterIsLower/>
          <ResultCell label="LOI" value={pred.loi} unit="%" accent="#FF3B30" baseline={EPS_BASE.loi}/>
          <ResultCell label="pHRR" value={pred.pHRR} unit="kW/m²" accent="#FF8C42" baseline={EPS_BASE.pHRR} betterIsLower/>
          <ResultCell label={t("eps.result.flexural", "Flexural")} value={pred.flexural} unit="kPa" accent="#39FF14" baseline={EPS_BASE.flexural}/>
          <ResultCell label={t("eps.result.tensile", "Tensile")} value={pred.tensile} unit="kPa" accent="#39FF14" baseline={EPS_BASE.tensile}/>
          <ResultCell label={t("eps.result.density", "Density")} value={pred.density} unit="kg/m³" accent="#FFFFFF" baseline={EPS_BASE.density}/>
        </div>

        {/* Sensitivity sparklines */}
        <div data-testid="sensitivity-grid">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-[var(--neon-cyan)]"/>
              <span className="font-mono text-xs uppercase tracking-[0.2em]"
                style={{ color: "#22D3EE", fontWeight: 700, opacity: 1 }}>
                {t("eps.sensitivity.title", "Sensitivity Trends")}
              </span>
            </div>
            <span className="font-mono text-[10px]"
              style={{ color: "#94A3B8", fontWeight: 500, opacity: 1 }}>
              {t("eps.sweep.hint", "Other additives constant · single-variable sweep")}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {Object.entries(ADDITIVES).map(([key, a]) => (
              <div key={key} className="rd-chart-container border border-[#1F1F1F] bg-[#0A0A0A] p-2" data-chart="true" data-chart-text-blue="true" style={{ backgroundColor: '#000000' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] uppercase tracking-wider"
                    style={{ color: '#FFFF00', WebkitTextFillColor: '#FFFF00', fontWeight: 700, opacity: 1 }}>
                    {a.short} · {t(a.nameKey || "", a.name)}
                  </span>
                  <span className="font-mono text-[10px]"
                    style={{ color: '#FFFF00', WebkitTextFillColor: '#FFFF00', fontWeight: 700, opacity: 1 }}>{mix[key].toFixed(2)}%</span>
                </div>
                <div className="space-y-1">
                  <Spark data={sweeps[key]} yKey="lambda" color="#00E5FF" label="λ (W/mK)" unit=""
                    currentX={mix[key]} format={(v) => v.toFixed(4)}/>
                  <Spark data={sweeps[key]} yKey="loi" color="#FF3B30" label="LOI (%)" unit=""
                    currentX={mix[key]} format={(v) => v.toFixed(1)}/>
                  <Spark data={sweeps[key]} yKey="flexural" color="#39FF14"
                    label={t("eps.spark.flexural", "Flexural (kPa)")} unit=""
                    currentX={mix[key]}/>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Professor's Notes — HNT + Bor */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="professor-notes-eps">
          <article className="tnote-container tnote-hnt" data-testid="prof-note-hnt" data-section="hnt-bor-card"
            style={{ border: '2px solid #000000', borderColor: '#000000', backgroundColor: '#FFD700' }}>
            <header className="tnote-header" style={{ backgroundColor: '#FFD700', borderBottom: '2px solid #000000' }}>
              <BookOpen className="w-3.5 h-3.5" style={{ color: '#000000' }}/>
              <span className="tnote-title" style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 700, opacity: 1 }}>
                {t("rdLab.note.hnt.title", "Teacher's Note · HNT Clay")}
              </span>
            </header>
            <div className="p-3 space-y-2" style={{ backgroundColor: '#FFD700' }}>
              <p className="tnote-body text-sm leading-relaxed">
                {t("rdLab.note.hnt.body", "Halloysite Nanotubes (HNT) are the natural nanostructured form of the clay family — tubular particles with inner diameters of 15–30 nm and outer diameters of 50–70 nm. When added to an EPS matrix at 2–5%:")}
              </p>
              <ul className="tnote-bullets font-mono text-[11px] space-y-1 leading-relaxed">
                <li>{t("rdLab.note.hnt.b1.pre", "→ Reduces fire spread via a silicate barrier by")} <span className="tnote-accent">{t("rdLab.note.hnt.b1.val", "30%+")}</span></li>
                <li>{t("rdLab.note.hnt.b2.pre", "→ Increases flexural strength by")} <span className="tnote-accent">{t("rdLab.note.hnt.b2.val", "18–25%")}</span></li>
                <li>{t("rdLab.note.hnt.b3", "→ Limits water vapour diffusion (μ ↑)")}</li>
                <li>{t("rdLab.note.hnt.b4", "→ Natural resource — low environmental impact, recyclable")}</li>
              </ul>
              <p className="tnote-italic text-xs italic">
                {t("rdLab.note.hnt.italic", "In our doctoral research, HNT-modified EPS was found to develop a carbonised layer 2.4× more efficiently than standard EPS at temperatures above 250 °C under flame exposure.")}
              </p>
            </div>
          </article>

          <article className="tnote-container tnote-bor" data-testid="prof-note-bor" data-section="hnt-bor-card"
            style={{ border: '2px solid #000000', borderColor: '#000000', backgroundColor: '#FFD700' }}>
            <header className="tnote-header" style={{ backgroundColor: '#FFD700', borderBottom: '2px solid #000000' }}>
              <BookOpen className="w-3.5 h-3.5" style={{ color: '#000000' }}/>
              <span className="tnote-title" style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 700, opacity: 1 }}>
                {t("rdLab.note.bor.title", "Teacher's Note · Boron Compounds")}
              </span>
            </header>
            <div className="p-3 space-y-2" style={{ backgroundColor: '#FFD700' }}>
              <p className="tnote-body text-sm leading-relaxed">
                {t("rdLab.note.bor.body.pre", "Türkiye holds")} <span className="tnote-accent">{t("rdLab.note.bor.body.val", "73%")}</span> {t("rdLab.note.bor.body.post", "of the world's boron reserves. Zinc borate (Zn₃B₆O₁₂·H₂O) and boric acid (H₃BO₃) are high-performance flame retardants for EPS:")}
              </p>
              <ul className="tnote-bullets font-mono text-[11px] space-y-1 leading-relaxed">
                <li>{t("rdLab.note.bor.b1", "→ Forms a glassy boroxide layer above 300 °C")}</li>
                <li>{t("rdLab.note.bor.b2.pre", "→ Can increase LOI by")} <span className="tnote-accent">{t("rdLab.note.bor.b2.val", "+12–15 points")}</span></li>
                <li>{t("rdLab.note.bor.b3.pre", "→ Thermal stability (TGA) shifts by")} <span className="tnote-accent">{t("rdLab.note.bor.b3.val", "+40 °C")}</span></li>
                <li>{t("rdLab.note.bor.b4.pre", "→ Reduces smoke production (SPR) by")} <span className="tnote-accent">{t("rdLab.note.bor.b4.val", "50%+")}</span></li>
                <li>{t("rdLab.note.bor.b5", "→ Halogen-free — RoHS / REACH compliant")}</li>
              </ul>
              <p className="tnote-italic text-xs italic">
                {t("rdLab.note.bor.italic", "Strategic insight: Türkiye's boron wealth lowers import dependency for domestically produced EPS panels, providing a strong cost advantage.")}
              </p>
            </div>
          </article>
        </div>

        {/* Disclaimer */}
        <div className="tnote-disclaimer p-3 flex items-start gap-2" data-testid="eps-disclaimer">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#FFCC00" }}/>
          <p className="tnote-disclaimer-body font-mono text-[11px] leading-relaxed">
            <strong>{t("eps.warn.label", "SIMULATION WARNING:")}</strong>{" "}
            {t("eps.warn.body", "These data are simulation predictions derived from academic research and literature trends. Schmied (2018) · Du (2010) · Stankovich (2006) · ZnB literature. Laboratory verification (ISO 5660-1, ASTM D3801, TS EN 12089) is mandatory before final production.")}
          </p>
        </div>

        {/* Pricing Widget */}
        <PricingWidget mix={mix}/>
      </div>
    </section>
  );
}
